import { IFlowStepSequence } from "./IFlowStepSequence";
import { FlowConfiguration } from "../FlowConfiguration";
import { FlowStepOutputInstructions, FlowStepOutputInstructionType } from "./FlowStepOutputInstructions";
import { FlowSequenceCompleteStrategy } from "./FlowSequenceCompleteStrategy";
import { FlowStepSequenceType } from "./FlowStepSequenceType";
import { FlowUtilities, StepWithPath } from "../Utilities/FlowUtilities";
import { FlowStepDataRegistration } from "../Utilities/RegisterFlowStepData";
import { IConfigStorage } from "../../Common/IConfigStorage";
import { DataUtilities } from "../../Common/DataUtilities";
import { FlowStepData } from "../Steps/FlowStepData";
import { FlowConstants } from "../Utilities/FlowConstants";
import { StateManager } from "../../Common/StateManager";
import { ExpressionConfiguration } from "../../Expressions/ExpressionConfiguration";
import { IDataDefinition } from "../../Common/Data/Definitions/IDataDefinition";
import { StringDataDefinition } from "../../Common/Data/Definitions/StringDataDefinition";
import { DataDefinitionUtilities } from "../../Common/DataDefinitionUtilities";
import { ValidationIssue } from "../../Common/IDesignValidationProvider";

export class FlowStepSequence implements IFlowStepSequence, IConfigStorage {
    static STEPS_CHANGED = "steps_changed";
    static COLLAPSING: string = "collapsing";
    static EXPANDING: string = "expanding";
    static CONNECTED: string = "connected";
    static SHOW_DESCRIPTION = "showDescription";
    static COLOR: string = "color";
    static DRAG_OVER: string = "isDraggingOver";
    static DRAG_START: string = "dragStart";
    static DRAG_END: string = "dragEnd";
    static IS_COLLAPSED: string = "isCollapsed";
    static CONNECTION_CHANGED: string = "connectionChanged";
    static MOVED: string = "moved";
    static ERROR_MESSAGE: string = "errorMessage";
    static STRATEGY: string = "strategy";
    static STEPS: string = "steps";
    static NAME: string = "name";
    static BRANCHES_CHANGED = "branchesChanged";
    _id: string = DataUtilities.generateUniqueId();
    name?: string;
    listArrayVariableName?: string;
    listItemVariableName: string = "item";
    private _stackColor: string = FlowConstants.DEFAULT_SEQUENCE_COLOR;

    constructor(color?: string) {
        this._branches.push({ steps: [] });

        let reg = FlowStepDataRegistration.getByTypeName("sequence_end_step");

        this._endStep = reg.create();
        this._endStep.sequence = this;
        this._endStep._id = `${this._id}_end`;
        this._endStep.getOutcomeInstructions()[0].strategy = FlowStepOutputInstructionType.END_FLOW;

        this.strategy = FlowSequenceCompleteStrategy.END_FLOW;

        if (color) {
            this._stackColor = color;
        }

        this.listenToEndStep();

        // this was added because the nodes were not invalidated when there was no flow edit context.
        StateManager.addObserver(
            this,
            () => {
                this.flow?.invalidateNodes();
            },
            [FlowStepSequence.STEPS_CHANGED],
        );
    }

    private _branches: { steps: FlowStepData[] }[] = [];

    get branches(): { steps: FlowStepData[] }[] {
        return this._branches;
    }

    private _sequenceType = FlowStepSequenceType.STANDARD;

    get sequenceType(): FlowStepSequenceType {
        return this._sequenceType;
    }

    set sequenceType(value: FlowStepSequenceType) {
        if (value === FlowStepSequenceType.PATH_CANDIDATE) {
            throw new Error("cannot switch type to be " + value);
        }

        this._sequenceType = value;
    }

    private _flow?: FlowConfiguration;

    get flow(): FlowConfiguration | undefined {
        return this._flow;
    }

    set flow(flow: FlowConfiguration | undefined) {
        this._flow = flow;

        for (let step of this.steps) {
            step.syncInputDataDefinition();
        }
    }

    private _x: number = -1;

    get x(): number {
        return this._x;
    }

    set x(value: number) {
        if (this._id === FlowConstants.PRIMARY_SEQUENCE && value !== FlowConstants.PRIMARY_SEQUENCE_POSITION.x)
            throw new Error("you cannot move the primary sequence (x)");

        let changed = false;
        if (this._x !== value) changed = true;

        this._x = value;

        if (changed) StateManager.propertyChanged(this, FlowStepSequence.MOVED);
    }

    private _y: number = -1;

    get y(): number {
        return this._y;
    }

    set y(value: number) {
        if (this._id === FlowConstants.PRIMARY_SEQUENCE && value !== FlowConstants.PRIMARY_SEQUENCE_POSITION.y)
            throw new Error("you cannot move the primary sequence (y)");

        let changed = false;
        if (this._y !== value) changed = true;

        this._y = value;

        if (changed) StateManager.propertyChanged(this, FlowStepSequence.MOVED);
    }

    private _desiredY: number = -1;

    get desiredY(): number {
        return this._desiredY;
    }

    set desiredY(value: number) {
        this._desiredY = value;
        this._y = value;
    }

    private _desiredX: number = -1;

    get desiredX(): number {
        return this._desiredX;
    }

    set desiredX(value: number) {
        this._desiredX = value;
        this._x = value;
    }

    private _height: number = FlowConstants.DEFAULT_STACK_HEIGHT;

    get height(): number {
        return this._height;
    }

    set height(value: number) {
        //let oldVal = this._height;
        this._height = value;
    }

    private _canDelete: boolean = true;

    get canDelete(): boolean {
        return this._canDelete;
    }

    set canDelete(d: boolean) {
        this._canDelete = d;
    }

    private _isCollapsed: boolean = false;

    get isCollapsed() {
        return this._isCollapsed;
    }

    set isCollapsed(collapsed: boolean) {
        this._isCollapsed = collapsed;
        if (collapsed) {
            StateManager.propertyChanged(this, FlowStepSequence.COLLAPSING);
        } else {
            StateManager.propertyChanged(this, FlowStepSequence.EXPANDING);
        }
        StateManager.propertyChanged(this, FlowStepSequence.IS_COLLAPSED);
    }

    private _canCopy: boolean = true;

    get canCopy(): boolean {
        return this._canCopy;
    }

    set canCopy(c: boolean) {
        this._canCopy = c;
    }

    private _endStep: FlowStepData;

    get endStep() {
        return this._endStep;
    }

    private _whileExpression?: ExpressionConfiguration;

    get whileExpression(): ExpressionConfiguration | undefined {
        if (this.sequenceType === FlowStepSequenceType.ITERATE_WHILE) {
            if (!this._whileExpression) this._whileExpression = new ExpressionConfiguration();

            this._whileExpression.inputData = () => FlowUtilities.getDataForSequence(this.flow!, this._id);
        }

        return this._whileExpression;
    }

    get errorMessage(): string {
        return this.endStep.getOutcomeInstructions()[0].errorMessage;
    }

    set errorMessage(message: string) {
        this.endStep.getOutcomeInstructions()[0].errorMessage = message;
    }

    get linkToSequence(): string {
        return this.endStep.getOutcomeInstructions()[0].connectedSequenceId!;
    }

    get strategy(): FlowSequenceCompleteStrategy {
        let hiddenStep = this.endStep;
        let ins = hiddenStep.getOutcomeInstructions()[0];

        switch (ins.strategy) {
            case FlowStepOutputInstructionType.BRANCH:
                return FlowSequenceCompleteStrategy.LINK;
            case FlowStepOutputInstructionType.END_FLOW:
                return FlowSequenceCompleteStrategy.END_FLOW;
            case FlowStepOutputInstructionType.THROW_EXCEPTION:
                return FlowSequenceCompleteStrategy.END_FLOW_IN_ERROR;
            case FlowStepOutputInstructionType.CONTINUE:
                return FlowSequenceCompleteStrategy.RETURN_TO_CALLING_STEP;
        }
    }

    set strategy(value: FlowSequenceCompleteStrategy) {
        let hiddenStep = this.endStep;
        let ins = hiddenStep.getOutcomeInstructions()[0];
        switch (value) {
            case FlowSequenceCompleteStrategy.END_FLOW:
                ins.strategy = FlowStepOutputInstructionType.END_FLOW;
                break;
            case FlowSequenceCompleteStrategy.LINK:
                ins.strategy = FlowStepOutputInstructionType.BRANCH;
                break;
            case FlowSequenceCompleteStrategy.END_FLOW_IN_ERROR:
                ins.strategy = FlowStepOutputInstructionType.THROW_EXCEPTION;
                break;
            case FlowSequenceCompleteStrategy.RETURN_TO_CALLING_STEP:
                if (!this.canReturn()) {
                    throw new Error("can not return");
                }

                ins.strategy = FlowStepOutputInstructionType.CONTINUE;
                break;
        }
        StateManager.propertyChanged(this, FlowStepSequence.CONNECTED);
        StateManager.propertyChanged(this, FlowStepSequence.STRATEGY);
    }

    get steps(): FlowStepData[] {
        return this.getSteps(0);
    }

    get allSteps(): readonly FlowStepData[] {
        let ret: FlowStepData[] = [];

        for (let b of this._branches) {
            ret.push(...b.steps);
        }

        ret.push(this.endStep);

        return ret;
    }

    get instructions(): readonly FlowStepOutputInstructions[] {
        return [...this.steps, this.endStep].reduce((insts: FlowStepOutputInstructions[], currentStep) => {
            insts.push(...currentStep.getOutcomeInstructions());
            return insts;
        }, []);
    }

    get color(): string {
        return this._stackColor;
    }

    set color(color: string) {
        this._stackColor = color;
    }

    get isError() {
        return this._id === FlowConstants.ERROR_SEQUENCE;
    }

    get isEnd() {
        return this._id === FlowConstants.END_SEQUENCE;
    }

    get isPrimary() {
        return this._id === FlowConstants.PRIMARY_SEQUENCE;
    }

    get isNormal(): boolean {
        return !this.isError && !this.isEnd;
    }

    get isOneStep() {
        return this.steps.length <= 1;
    }

    allStepsWithoutEnd(): readonly FlowStepData[] {
        let ret: FlowStepData[] = [];

        for (let b of this._branches) {
            ret.push(...b.steps);
        }

        return ret;
    }

    addBranch() {
        this._branches.push({ steps: [] });
        StateManager.propertyChanged(this, FlowStepSequence.BRANCHES_CHANGED);
    }

    removeBranch(which: number) {
        this._branches.splice(which, 1);
        StateManager.propertyChanged(this, FlowStepSequence.BRANCHES_CHANGED);
    }

    getAddedData(): IDataDefinition[] | undefined {
        if (this._id === FlowConstants.ERROR_SEQUENCE) {
            let dd: IDataDefinition[] = [];
            dd.push(new StringDataDefinition(FlowConstants.ERROR_MESSAGE_DATA_NAME));
            dd.push(new StringDataDefinition(FlowConstants.ERROR_STACK_DATA_NAME));
            dd.push(new StringDataDefinition(FlowConstants.ERROR_STEP_ID));
            dd.push(new StringDataDefinition(FlowConstants.ERROR_STEP_NAME));
            dd.push(new StringDataDefinition(FlowConstants.ERROR_STEP_TYPE));
            return dd;
        }

        if (
            this.sequenceType === FlowStepSequenceType.ITERATE_PARALLEL ||
            this.sequenceType === FlowStepSequenceType.ITERATE
        ) {
            let dd: IDataDefinition[] = [];
            if (this.listArrayVariableName) {
                let def = DataUtilities.getFieldFromDataDefinitionByName(
                    FlowUtilities.getDataForSequence(this.flow!, this._id!).fields,
                    this.listArrayVariableName,
                );
                if (def) {
                    let newDef = DataDefinitionUtilities.getDataDefinitionFromData(def.getStorageData());
                    newDef!.name = this.listItemVariableName;
                    newDef!.list = false;
                    dd.push(newDef!);
                }
            }
            return dd;
        }

        return undefined;
    }

    getInboundConnections(): StepWithPath[] {
        let inboundConnections: StepWithPath[] = [];
        if (this.flow) {
            for (let seq of this.flow.allSequences) {
                for (let s of seq.allSteps) {
                    let insts = s.getOutcomeInstructions();

                    for (let ins of insts) {
                        if (
                            ins.connectedSequenceId === this._id &&
                            ins.strategy === FlowStepOutputInstructionType.BRANCH
                        ) {
                            inboundConnections.push({
                                step: s,
                                path: ins.pathName,
                            });
                        }
                    }

                    if (inboundConnections.length) {
                        break;
                    }
                }
            }
        }

        return inboundConnections;
    }

    canBeForIterationSequence(): boolean {
        // is not primary
        if (this.isPrimary) {
            return false;
        }

        // find all inbound connections into this sequence
        // if more than one, cannot be iteration
        let inboundConnections = this.getInboundConnections();
        if (inboundConnections.length > 1 || inboundConnections.length === 0) {
            return false;
        }

        return inboundConnections[0].path === FlowConstants.ITERATION_PATH_NAME;
    }

    canReturn(): boolean {
        let inboundConnections = this.getInboundConnections();

        if (inboundConnections.length !== 1) {
            // do not know to return which step.
            return false;
        }

        return !FlowUtilities.IsEndStep(inboundConnections[0].step);
    }

    getElementId(): string {
        return this._id;
    }

    reset(): void {
        this._x = this.desiredX;
        this._y = this.desiredY;
    }

    getSteps(branch: number = 0): FlowStepData[] {
        let steps = this._branches[branch];
        if (!steps) throw new Error("Branch does not exist " + branch);
        return steps.steps;
    }

    removeStep(step: FlowStepData): void {
        step.sequence = undefined;
        for (let b of this.branches) {
            b.steps = b.steps.filter((s) => {
                return s !== step;
            });
        }

        StateManager.propertyChanged(this, FlowStepSequence.STEPS_CHANGED);
    }

    find(stepId: string): FlowStepData | null {
        for (let b of this._branches) {
            for (let s of [...b.steps, this.endStep]) {
                if (s._id === stepId) return s;
            }
        }
        return null;
    }

    addStep(step: FlowStepData, index?: number, branch: number = 0) {
        let localsteps = this.getSteps(branch);
        let b = this._branches[branch];
        // add step to last
        if (index === undefined || index === null || this.isCollapsed) {
            index = localsteps.length;
        }

        if (!this.isNormal && step.getOutcomeInstructions().length > 1) {
            throw new Error(`You cannot add Multi Output Step in the ${this.isError ? "Error" : "End"} stack.`);
        } else {
            localsteps.splice(index, 0, step);
            b.steps = localsteps;
            step.sequence = this;
            StateManager.propertyChanged(this, FlowStepSequence.STEPS_CHANGED);
        }
    }

    moveStep(step: FlowStepData, index: number, fromBranch: number = 0, toBranch: number = 0) {
        let fromLocalSteps = this.getSteps(fromBranch);
        let toLocalSteps = this.getSteps(toBranch);

        let originIdx = fromLocalSteps.indexOf(step);
        if (originIdx < 0) return;
        fromLocalSteps.splice(originIdx, 1);
        if (index === undefined || index === null) {
            index = toLocalSteps.length;
        }
        toLocalSteps.splice(index, 0, step);
        StateManager.propertyChanged(this, FlowStepSequence.STEPS_CHANGED);
    }

    toggleShowDescriptionForSteps(show: boolean) {
        for (let b of this._branches) {
            for (let step of b.steps) {
                if (step.description !== "") {
                    if (step.shouldShowDescription !== show) {
                        step.shouldShowDescription = show;
                        StateManager.propertyChanged(step, FlowStepSequence.SHOW_DESCRIPTION);
                    }
                }
            }
        }
    }

    getStorageData(): object {
        let data = {};

        Reflect.set(data, "sequenceType", this._sequenceType);
        Reflect.set(data, "_id", this._id);
        Reflect.set(data, "name", this.name);
        Reflect.set(data, "_x", this._x);
        Reflect.set(data, "_y", this._y);
        Reflect.set(data, "_desiredY", this._desiredY);
        Reflect.set(data, "_desiredX", this._desiredX);
        Reflect.set(data, "_height", this._height);
        Reflect.set(data, "_stackColor", this._stackColor);
        Reflect.set(data, "_canDelete", this._canDelete);
        Reflect.set(data, "_isCollapsed", this._isCollapsed);
        Reflect.set(data, "_canCopy", this._canCopy);

        Reflect.set(data, "_endStep", this._endStep.getStorageData());

        this.listenToEndStep();

        Reflect.set(data, "listArrayVariableName", this.listArrayVariableName);
        Reflect.set(data, "listItemVariableName", this.listItemVariableName);

        if (this._whileExpression) {
            Reflect.set(data, "whileExpression", this._whileExpression.getStorageData());
        }
        Reflect.set(data, "listArrayVariableName", this.listArrayVariableName);
        Reflect.set(data, "listItemVariableName", this.listItemVariableName);

        let branches: object[] = [];

        for (let original in this._branches) {
            branches.push({
                original,
                steps: this._branches[original].steps.map((s) => s.getStorageData()),
            });
        }

        Reflect.set(data, "branches", branches);

        return data;
    }

    setStorageData(data: object): void {
        this._sequenceType = Reflect.get(data, "sequenceType");
        this._id = Reflect.get(data, "_id");
        this.name = Reflect.get(data, "name");
        this._x = Reflect.get(data, "_x");
        this._y = Reflect.get(data, "_y");
        this._desiredY = Reflect.get(data, "_desiredY");
        this._desiredX = Reflect.get(data, "_desiredX");
        this._height = Reflect.get(data, "_height");
        this._stackColor = Reflect.get(data, "_stackColor");
        this._canDelete = Reflect.get(data, "_canDelete");
        this._isCollapsed = Reflect.get(data, "_isCollapsed");

        this.listArrayVariableName = Reflect.get(data, "listArrayVariableName");
        this.listItemVariableName = Reflect.get(data, "listItemVariableName");

        this.endStep.setStorageData(Reflect.get(data, "_endStep"));

        let branches = Reflect.get(data, "branches");

        let localBranches: { steps: FlowStepData[] }[] = [];

        for (let branch of branches) {
            let steps = branch.steps.map((stepData: FlowStepData) => {
                let type = stepData.__type;

                if (!type) {
                    throw new Error(`_type is required`);
                }

                let registeredControl = FlowStepDataRegistration.getByTypeName(type);

                if (!registeredControl) {
                    throw new Error(`_type[${type}] is invalid`);
                }

                let val = registeredControl.create();
                val.setStorageData(stepData);

                val.sequence = this;

                return val;
            });
            localBranches.push({ steps: steps });
        }

        this._branches = localBranches;

        let expressionData = Reflect.get(data, "whileExpression");
        if (expressionData) {
            this._whileExpression = new ExpressionConfiguration();
            this._whileExpression.setStorageData(expressionData);
        }

        this.listArrayVariableName = Reflect.get(data, "listArrayVariableName");
        this.listItemVariableName = Reflect.get(data, "listItemVariableName");
    }

    getValidationIssues(): ValidationIssue[] {
        let result: ValidationIssue[] = [];

        if (this.steps.length === 0 && !this.isEnd && !this.isError) {
            result.push(new ValidationIssue(`[${this.name || this._id}]: sequence should have one step at least`));
        }

        // connected check
        if (this.flow && !this.isPrimary && !this.isEnd && !this.isError) {
            let connected = false;
            for (let seq of this.flow.allSequences) {
                for (let step of seq.allSteps) {
                    for (let inst of step.getOutcomeInstructions()) {
                        if (inst.connectedSequenceId === this._id) {
                            connected = true;
                            break;
                        }
                    }
                    if (connected) break;
                }
                if (connected) break;
            }

            if (!connected) {
                result.push(new ValidationIssue(`[${this.name || this._id}]: sequence is not connected`));
            }
        }

        return result;
    }

    getStepAfter(step: FlowStepData, includeEndStep: boolean = false): FlowStepData | undefined {
        if (step === this.endStep) return;

        for (let b of this._branches) {
            let index = b.steps.indexOf(step);

            if (index !== -1) {
                // step is in this branch
                if (index >= b.steps.length - 1) {
                    // its the last one
                    if (includeEndStep) return this.endStep;
                    return undefined;
                } else {
                    return b.steps[index + 1];
                }
            }
        }
        // none found
        return;
    }

    private listenToEndStep() {
        StateManager.addObserver(this.endStep.getOutcomeInstructions()[0], () => {
            StateManager.propertyChanged(this, FlowStepSequence.STRATEGY);
        });
    }
}
